/* R for Rabbit v4 motion API (DESIGN-v4.md section 4). Classic deferred script, no library, loaded on
   every page before rfr.js (layout/theme.liquid). Styles live in assets/rfr-motion.css.

   HOW TO USE (builders B, C, D)
   Markup (no JS needed on your side):
     <h2 data-rfr-reveal>                 fades + rises 16px once it enters the viewport (640ms, ease-out)
     <div data-rfr-reveal="scale">        fades + scales 0.96 -> 1
     <ul data-rfr-stagger>                its direct children reveal 60ms apart (max 8 steps)
     <span data-rfr-count>517</span>      counts up from 0 once visible; keeps the text format ("4.7", "1,234")
                                          ONLY for real numbers already on the page
     <div data-rfr-inview>                adds class .rfr-in once it enters the viewport (never hides anything)
     <section data-rfr-progress>          sets --rfr-progress (0..1) while it crosses the viewport
       data-rfr-progress="contain"        0 when its top reaches the viewport top, 1 when its bottom reaches the
                                          viewport bottom (the pinned phase of a tall sticky chapter)
       add the word "always" to run even where CSS scroll timelines exist (default: fallback only)
       data-rfr-steps="4"                 also sets data-rfr-step="0".."3" (style states with attribute selectors)
     <span class="rfr-hop" aria-hidden="true"><i></i><i></i><i></i></span>   hopping 3-dot loader
                                          (RFR.hopHTML is the same string); pair it with visible or sr-only text
     class="rfr-bump"                     one spring pop (or call RFR.bump(el))
   JS:
     RFR.reduced          true when the shopper asked for reduced motion (live)
     RFR.scroller         the element that actually scrolls: .page-wrapper at >= 990px (Horizon's squeeze
                          layout), the document below. ALWAYS use this, never window.scrollY on desktop.
     RFR.scrollTop()      current scroll position of RFR.scroller
     RFR.onScroll(fn)     fn(y, dy) inside the ONE shared rAF scroll loop (passive; nothing runs when hidden)
     RFR.flyToCart(img)   two-hop arc of a copy of img into the header cart, then the cart springs.
                          Returns a Promise. Runs automatically for every add to cart (PDP, sticky bar, quick
                          add, quick add modal): you do not need to call it for those.
     RFR.bump(el)         spring pop on any element
     RFR.observe(root)    re-scan root for data-rfr-* (new DOM is also picked up by a MutationObserver)
   Reduced motion: nothing hides, nothing moves, counts show their final number, the loader pulses.
   Never put data-rfr-reveal on the hero or anything that must be readable at first paint. */
(() => {
  const d = document;
  const html = d.documentElement;
  const RFR = (window.RFR = window.RFR || {});
  const mqReduce = matchMedia('(prefers-reduced-motion: reduce)');
  const mqSqueeze = matchMedia('(min-width: 990px)');
  const timeline = CSS.supports('animation-timeline: view()');
  const SEL = '[data-rfr-reveal],[data-rfr-stagger],[data-rfr-count],[data-rfr-progress],[data-rfr-inview]';

  Object.defineProperty(RFR, 'reduced', { get: () => mqReduce.matches, configurable: true });
  Object.defineProperty(RFR, 'scroller', {
    get: () => (mqSqueeze.matches && d.querySelector('.page-wrapper')) || d.scrollingElement || html,
    configurable: true,
  });
  RFR.scrollTop = () => RFR.scroller.scrollTop;
  RFR.supportsTimeline = timeline;
  RFR.hopHTML = '<span class="rfr-hop" aria-hidden="true"><i></i><i></i><i></i></span>';

  /* ---------- One shared scroll loop ---------- */
  const subs = [];
  let lastY = RFR.scrollTop();
  let queued = false;
  const frame = () => {
    queued = false;
    const y = RFR.scrollTop();
    const dy = y - lastY;
    lastY = y;
    for (const fn of subs) fn(y, dy);
  };
  d.addEventListener(
    'scroll',
    (event) => {
      const t = event.target;
      if (t !== d && t !== html && t !== RFR.scroller) return;
      if (!queued) {
        queued = true;
        requestAnimationFrame(frame);
      }
    },
    { capture: true, passive: true }
  );
  RFR.onScroll = (fn) => {
    subs.push(fn);
    fn(lastY, 0);
  };

  /* Header + tab bar state for CSS: data-rfr-scrolled (past 8px), data-rfr-dir="down" after 160px. */
  let dir = '';
  RFR.onScroll((y, dy) => {
    html.toggleAttribute('data-rfr-scrolled', y > 8);
    let next = dir;
    if (y < 160) next = '';
    else if (dy > 6) next = 'down';
    else if (dy < -6) next = 'up';
    if (next !== dir) {
      dir = next;
      if (dir) html.dataset.rfrDir = dir;
      else delete html.dataset.rfrDir;
    }
  });

  /* ---------- Spring pop ---------- */
  RFR.bump = (el) => {
    if (!el || RFR.reduced || !el.animate) return;
    el.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.22)', offset: 0.35 },
        { transform: 'scale(0.94)', offset: 0.7 },
        { transform: 'scale(1)' },
      ],
      { duration: 520, easing: 'ease-out' }
    );
  };

  /* ---------- Reveal, stagger, count, progress ---------- */
  const fmt = (n, dec, group) =>
    n.toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec, useGrouping: group });

  const count = (el) => {
    const text = el.textContent;
    const m = text.match(/\d[\d,]*(\.\d+)?/);
    if (!m || RFR.reduced) return;
    const final = parseFloat(m[0].replace(/,/g, ''));
    const dec = m[1] ? m[1].length - 1 : 0;
    const group = m[0].includes(',');
    const pre = text.slice(0, m.index);
    const post = text.slice(m.index + m[0].length);
    /* Hold the final width so neighbours never move (no layout shift). */
    el.style.display = 'inline-block';
    el.style.fontVariantNumeric = 'tabular-nums';
    el.style.minWidth = el.getBoundingClientRect().width + 'px';
    const t0 = performance.now();
    const dur = 900;
    const step = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      el.textContent = p < 1 ? pre + fmt(final * e, dec, group) + post : text;
      if (p < 1) requestAnimationFrame(step);
    };
    el.textContent = pre + fmt(0, dec, group) + post;
    requestAnimationFrame(step);
  };

  const reveal = (el) => {
    el.classList.remove('rfr-pending');
    if (el.matches('[data-rfr-reveal],[data-rfr-stagger],[data-rfr-inview]')) el.classList.add('rfr-in');
    if (el.hasAttribute('data-rfr-count')) count(el);
  };
  RFR.reveal = reveal;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        reveal(entry.target);
      }
    },
    { rootMargin: '0px 0px -12% 0px' }
  );

  const live = new Set();
  const progressIO = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) entry.isIntersecting ? live.add(entry.target) : live.delete(entry.target);
      queued || requestAnimationFrame(frame);
    },
    { rootMargin: '25% 0px' }
  );
  const setProgress = (el) => {
    const r = el.getBoundingClientRect();
    const vh = innerHeight;
    let p;
    if ((el.dataset.rfrProgress || '').includes('contain')) {
      p = r.height > vh ? -r.top / (r.height - vh) : (vh - r.bottom) / (vh - r.height || 1);
    } else {
      p = (vh - r.top) / (vh + r.height);
    }
    p = Math.min(1, Math.max(0, p));
    el.style.setProperty('--rfr-progress', p.toFixed(3));
    const n = Number(el.dataset.rfrSteps);
    if (n > 0) {
      const s = String(Math.min(n - 1, Math.floor(p * n)));
      if (el.dataset.rfrStep !== s) el.dataset.rfrStep = s;
    }
  };
  RFR.onScroll(() => live.forEach(setProgress));

  const design = !!(window.Shopify && window.Shopify.designMode);
  const init = (el) => {
    if (el.__rfr) return;
    el.__rfr = true;
    if (el.hasAttribute('data-rfr-progress')) {
      const mode = el.dataset.rfrProgress || '';
      if (!timeline || mode.includes('always')) {
        progressIO.observe(el);
        setProgress(el);
      }
    }
    if (!el.matches('[data-rfr-reveal],[data-rfr-stagger],[data-rfr-count],[data-rfr-inview]')) return;
    if (RFR.reduced || design) {
      el.classList.remove('rfr-pending');
      if (el.hasAttribute('data-rfr-inview')) el.classList.add('rfr-in');
      return;
    }
    if (el.matches('[data-rfr-reveal],[data-rfr-stagger]')) el.classList.add('rfr-pending');
    io.observe(el);
  };
  RFR.observe = (root = d) => {
    if (root.nodeType !== 1 && root !== d) return;
    if (root !== d && root.matches(SEL)) init(root);
    root.querySelectorAll(SEL).forEach(init);
  };

  RFR.observe(d);
  html.classList.add('rfr-ready');

  new MutationObserver((records) => {
    for (const r of records) {
      for (const node of r.addedNodes) {
        if (node.nodeType === 1 && (node.matches(SEL) || node.querySelector(SEL))) RFR.observe(node);
      }
    }
  }).observe(d.body, { childList: true, subtree: true });

  /* ---------- Hop to cart ---------- */
  const visibleImg = (root) => {
    if (!root) return null;
    for (const img of root.querySelectorAll('img')) {
      const r = img.getBoundingClientRect();
      if (r.width > 40 && r.height > 40 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth) {
        return img;
      }
    }
    return null;
  };

  const translateY = (el) => {
    const t = el && getComputedStyle(el).translate;
    if (!t || t === 'none') return 0;
    const parts = t.split(' ');
    return parseFloat(parts[1] || '0') || 0;
  };

  let flying = 0;
  RFR.flyToCart = (src) => {
    const img = src && src.tagName !== 'IMG' ? src.querySelector('img') : src;
    const target = d.querySelector('#header-component .header-actions__cart-icon, .header-actions__cart-icon');
    const done = () => {
      d.querySelectorAll('.header-actions__cart-icon, .rfr-tab__cart').forEach(RFR.bump);
    };
    if (!target) return Promise.resolve();
    if (Date.now() - flying < 700) return RFR.flight || Promise.resolve();
    if (RFR.reduced || !img || !img.animate) {
      done();
      return Promise.resolve();
    }
    flying = Date.now();
    /* Bring the header back if it is hidden, and aim at where the cart icon will rest. */
    html.classList.add('rfr-chrome-pin');
    const tr = target.getBoundingClientRect();
    const restTop = tr.top - translateY(target.closest('.header-section'));
    const sr = img.getBoundingClientRect();
    const size = Math.max(48, Math.min(200, sr.width, sr.height));
    const s = { x: sr.left + sr.width / 2, y: sr.top + sr.height / 2 };
    const e = { x: tr.left + tr.width / 2, y: restTop + tr.height / 2 };
    /* Hop 1 lands a little under the cart; hop 2 is the short jump up into it. */
    const m = {
      x: s.x + (e.x - s.x) * 0.62,
      y: s.y > e.y + 90 ? e.y + Math.max(80, (s.y - e.y) * 0.3) : s.y + (e.y - s.y) * 0.62,
    };
    const dist = Math.hypot(e.x - s.x, e.y - s.y);
    const end = 26 / size;
    const midScale = 0.42;
    const ease = (p) => 0.5 - Math.cos(Math.PI * p) / 2;
    /* Arc heights, lowered where an arc would leave the top of the screen. */
    const room = (a, b, sc) => Math.max(0, (a + b) / 2 - (size * sc) / 2 - 10);
    const h1 = Math.min(150, Math.max(48, dist * 0.22), room(s.y, m.y, (1 + midScale) / 2));
    const h2 = Math.min(h1 * 0.45, 44, room(m.y, e.y, (midScale + end) / 2) + 24);

    const clone = new Image();
    clone.src = img.currentSrc || img.src;
    clone.alt = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.className = 'rfr-hopclone';
    clone.style.cssText = `width:${size}px;height:${size}px;left:${s.x - size / 2}px;top:${s.y - size / 2}px`;
    d.body.append(clone);

    const frames = [];
    const N = 36;
    for (let i = 0; i <= N; i++) {
      const t = i / N;
      let x, y, sc, sx = 1, sy = 1;
      if (t <= 0.6) {
        const p = ease(t / 0.6);
        x = s.x + (m.x - s.x) * p;
        y = s.y + (m.y - s.y) * p - h1 * 4 * p * (1 - p);
        sc = 1 + (midScale - 1) * p;
      } else {
        const p = ease((t - 0.6) / 0.4);
        x = m.x + (e.x - m.x) * p;
        y = m.y + (e.y - m.y) * p - h2 * 4 * p * (1 - p);
        sc = midScale + (end - midScale) * p;
      }
      /* a small squash where the first hop lands */
      if (Math.abs(t - 0.6) < 0.03) {
        sx = 1.12;
        sy = 0.86;
      }
      frames.push({
        transform: `translate(${(x - s.x).toFixed(1)}px,${(y - s.y).toFixed(1)}px) scale(${(sc * sx).toFixed(3)},${(sc * sy).toFixed(3)})`,
        opacity: t > 0.9 ? 1 - (t - 0.9) * 6 : 1,
      });
    }
    const anim = clone.animate(frames, { duration: 650, easing: 'linear', fill: 'forwards' });
    return (RFR.flight = anim.finished)
      .catch(() => {})
      .then(() => {
        clone.remove();
        done();
        setTimeout(() => html.classList.remove('rfr-chrome-pin'), 900);
      });
  };

  /* Every add to cart: fly from the product's photo, then let the cart drawer open (Horizon would open
     it at once and its <dialog> sits in the top layer, which would hide the hop). */
  let lastCardImg = null;
  d.addEventListener(
    'click',
    (event) => {
      const t = event.target instanceof Element ? event.target : null;
      const card = t && t.closest('product-card');
      if (card && t.closest('.quick-add__button, [data-quick-add-trigger], button')) lastCardImg = visibleImg(card);
    },
    true
  );
  const sourceImg = (t) => {
    if (!(t instanceof Element)) return null;
    const card = t.closest('product-card');
    if (card) return visibleImg(card);
    if (t.closest('dialog')) return lastCardImg && lastCardImg.isConnected ? lastCardImg : null;
    const sec = t.closest('.shopify-section') || d.querySelector('main');
    return visibleImg(sec && sec.querySelector('media-gallery, .product-information__media')) ||
      visibleImg(d.querySelector('sticky-add-to-cart')) ||
      visibleImg(sec);
  };
  /* Quick add: the hop loader shows on the button while the request runs (data-rfr-busy on any add button,
     so other builders can style their own). */
  d.addEventListener(
    'shopify:cart:lines-update',
    (event) => {
      const t = event.target;
      if (event.action !== 'add' || !event.promise || !(t instanceof Element)) return;
      const btn = t.querySelector('[ref="addToCartButton"], .add-to-cart-button');
      if (!btn) return;
      btn.setAttribute('data-rfr-busy', '');
      let hop = null;
      if (btn.classList.contains('quick-add__button')) {
        btn.insertAdjacentHTML('beforeend', RFR.hopHTML);
        hop = btn.lastElementChild;
      }
      const clear = () => {
        btn.removeAttribute('data-rfr-busy');
        hop && hop.remove();
      };
      event.promise.then(clear, clear);
    },
    true
  );

  d.addEventListener(
    'shopify:cart:lines-update',
    (event) => {
      if (event.action !== 'add' || RFR.reduced || !event.promise) return;
      const t = event.target;
      const modal = t instanceof Element ? t.closest('dialog') : null;
      const img = sourceImg(t);
      /* Below 990px the drawer is a modal (top layer, would hide the hop): hold it until the hop lands.
         At 990px and up Horizon opens it as a side panel that squeezes the page; holding it would turn that
         squeeze into a layout shift after the click (CLS), so it opens at once and the hop flies above it. */
      const drawer = mqSqueeze.matches ? null : d.querySelector('cart-drawer-component[auto-open]');
      if (drawer) drawer.removeAttribute('auto-open');
      const openDrawer = () => {
        if (!drawer) return;
        drawer.setAttribute('auto-open', '');
        const themeDrawer = drawer.closest('theme-drawer');
        if (themeDrawer && !themeDrawer.hasAttribute('open') && typeof themeDrawer.open === 'function') themeDrawer.open();
      };
      event.promise
        .then((result) => {
          if (result && result.detail && result.detail.didError) {
            if (drawer) drawer.setAttribute('auto-open', '');
            return;
          }
          let went = false;
          const go = () => {
            if (went) return;
            went = true;
            if (img) {
              /* desktop: let the drawer's squeeze settle first, so the cart icon is where it will stay */
              const wait = mqSqueeze.matches ? 220 : 0;
              setTimeout(() => RFR.flyToCart(sourceImg(t) || img).then(openDrawer), wait);
              return;
            }
            /* No photo on screen here (e.g. phone product page): a builder may fly its own copy
               (rfr-product.js); open the drawer after that flight, or straight away if none starts. */
            setTimeout(() => {
              const f = Date.now() - flying < 700 && RFR.flight;
              (f || Promise.resolve()).then(openDrawer, openDrawer);
            }, 120);
          };
          if (modal && modal.open) {
            modal.addEventListener('close', () => setTimeout(go, 60), { once: true });
            setTimeout(go, 2000);
          } else go();
        })
        .catch(() => drawer && drawer.setAttribute('auto-open', ''));
    },
    true
  );

  /* ---------- Back to top: [data-rfr-top] eases the page up, then the logo does one small hop ---------- */
  d.addEventListener('click', (event) => {
    const btn = event.target instanceof Element && event.target.closest('[data-rfr-top]');
    if (!btn) return;
    event.preventDefault();
    const sc = RFR.scroller;
    const logo = [...d.querySelectorAll('#header-component .header-logo, #header-component a[href="/"]')].find(
      (el) => el.getClientRects().length > 0
    );
    const land = () => {
      html.classList.add('rfr-chrome-pin');
      if (logo && !RFR.reduced && logo.animate) {
        logo.animate(
          [
            { transform: 'translateY(0)' },
            { transform: 'translateY(-7px)', offset: 0.4, easing: 'cubic-bezier(.55,0,1,.45)' },
            { transform: 'translateY(0)' },
          ],
          { duration: 460, easing: 'cubic-bezier(.22,1,.36,1)' }
        );
      }
      (logo || d.querySelector('#MainContent'))?.focus?.({ preventScroll: true });
      setTimeout(() => html.classList.remove('rfr-chrome-pin'), 1200);
    };
    if (RFR.reduced) {
      sc.scrollTop = 0;
      land();
      return;
    }
    let fired = false;
    const once = () => {
      if (fired) return;
      fired = true;
      land();
    };
    (sc === d.scrollingElement ? d : sc).addEventListener('scrollend', once, { once: true });
    setTimeout(once, 1400);
    sc.scrollTo({ top: 0, behavior: 'smooth' });
  });
})();
